import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from '../../components/Sidebar';
import ProfileDropdown from '../../components/ProfileDropdown';
import API_BASE from '../../utils/api';
import { fetchDefaultAcademicYear, computedAcademicYearFallback } from '../../utils/academicYear';

const UNIFORM_TYPES = {
  shirt: '👔 Shirt', pant: '👖 Pant', skirt: '👗 Skirt', tie: '🎀 Tie', belt: 'Belt',
  socks: '🧦 Socks', shoes: '👟 Shoes', blazer: '🧥 Blazer', sweater: '🧶 Sweater',
  pe_shirt: '🏃 PE Shirt', pe_pant: '🏃 PE Pant', other: 'Other'
};

const PrincipalMaterials = () => {
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('books');
  // Seeded with the computed fallback for the very first paint; replaced by
  // the school's configured Settings → Academic Year as soon as it loads.
  const [defaultAcademicYear, setDefaultAcademicYear] = useState(computedAcademicYearFallback());

  const [formData, setFormData] = useState({
    classId: '',
    academicYear: defaultAcademicYear,
    books: [{ name: '', subject: '', author: '', publisher: '', price: 0, isRequired: true }],
    uniformItems: [],
    notes: ''
  });

  useEffect(() => { fetchClasses(); fetchDefaultAcademicYear().then(setDefaultAcademicYear); }, []);
  useEffect(() => { if (selectedClass) fetchMaterials(); }, [selectedClass]);

  const fetchClasses = async () => {
    try {
      const res = await axios.get(`${API_BASE}/classes`);
      setClasses(res.data);
      if (res.data.length > 0) setSelectedClass(res.data[0]._id);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchMaterials = async () => {
    try {
      const res = await axios.get(`${API_BASE}/materials?classId=${selectedClass}`);
      setMaterials(res.data);
    } catch (err) { console.error(err); }
  };

  const openCreateModal = (existing = null) => {
    if (existing) {
      setFormData({
        classId: existing.classId?._id || existing.classId,
        academicYear: existing.academicYear,
        books: existing.books.length > 0 ? existing.books : [{ name: '', subject: '', author: '', publisher: '', price: 0, isRequired: true }],
        uniformItems: existing.uniformItems || [],
        notes: existing.notes || ''
      });
    } else {
      setFormData({
        classId: selectedClass,
        academicYear: defaultAcademicYear,
        books: [{ name: '', subject: '', author: '', publisher: '', price: 0, isRequired: true }],
        uniformItems: [],
        notes: ''
      });
    }
    setShowModal(true);
    setError('');
    setActiveTab('books');
  };

  // Book helpers
  const addBook = () => setFormData(prev => ({
    ...prev, books: [...prev.books, { name: '', subject: '', author: '', publisher: '', price: 0, isRequired: true }]
  }));
  const updateBook = (idx, field, value) => setFormData(prev => {
    const books = [...prev.books];
    books[idx] = { ...books[idx], [field]: value };
    return { ...prev, books };
  });
  const removeBook = (idx) => setFormData(prev => ({
    ...prev, books: prev.books.filter((_, i) => i !== idx)
  }));

  // Uniform helpers
  const addUniformItem = () => setFormData(prev => ({
    ...prev, uniformItems: [...prev.uniformItems, {
      itemName: '', type: 'shirt', gender: 'all', availableSizes: [{ size: '', price: 0 }], color: '', isRequired: true
    }]
  }));
  const updateUniform = (idx, field, value) => setFormData(prev => {
    const items = [...prev.uniformItems];
    items[idx] = { ...items[idx], [field]: value };
    return { ...prev, uniformItems: items };
  });
  const removeUniform = (idx) => setFormData(prev => ({
    ...prev, uniformItems: prev.uniformItems.filter((_, i) => i !== idx)
  }));
  const addSize = (itemIdx) => setFormData(prev => {
    const items = [...prev.uniformItems];
    items[itemIdx] = { ...items[itemIdx], availableSizes: [...items[itemIdx].availableSizes, { size: '', price: 0 }] };
    return { ...prev, uniformItems: items };
  });
  const updateSize = (itemIdx, sizeIdx, field, value) => setFormData(prev => {
    const items = [...prev.uniformItems];
    const sizes = [...items[itemIdx].availableSizes];
    sizes[sizeIdx] = { ...sizes[sizeIdx], [field]: value };
    items[itemIdx] = { ...items[itemIdx], availableSizes: sizes };
    return { ...prev, uniformItems: items };
  });
  const removeSize = (itemIdx, sizeIdx) => setFormData(prev => {
    const items = [...prev.uniformItems];
    items[itemIdx] = { ...items[itemIdx], availableSizes: items[itemIdx].availableSizes.filter((_, i) => i !== sizeIdx) };
    return { ...prev, uniformItems: items };
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');

    // Validation: Check that all uniforms have at least one size with a non-empty size field
    for (const item of formData.uniformItems) {
      if (!item.itemName || !item.type) {
        setError('All uniform items must have a name and type');
        return;
      }
      if (item.availableSizes.length === 0) {
        setError(`"${item.itemName}" must have at least one size`);
        return;
      }
      for (const size of item.availableSizes) {
        if (!size.size || size.size.trim() === '') {
          setError(`All sizes for "${item.itemName}" must be filled in`);
          return;
        }
      }
    }

    try {
      await axios.post(`${API_BASE}/materials`, formData);
      setSuccess('Materials saved successfully!');
      setShowModal(false);
      fetchMaterials();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete these materials?')) return;
    try {
      await axios.delete(`${API_BASE}/materials/${id}`);
      fetchMaterials();
      setSuccess('Deleted successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError('Failed to delete'); }
  };

  const totalBooksCost = (books) => books.reduce((sum, b) => sum + (Number(b.price) || 0), 0);

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 lg:ml-64">
        <header className="bg-white shadow-sm border-b px-6 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">📚 Books & Uniform</h1>
            <p className="text-sm text-gray-500">Manage books, dress & shoes by class</p>
          </div>
          <ProfileDropdown />
        </header>

        <main className="p-6">
          {success && <div className="bg-green-50 border-l-4 border-green-500 text-green-700 p-3 rounded mb-4">{success}</div>}
          {error && <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-3 rounded mb-4">{error}</div>}

          <div className="flex flex-wrap gap-4 items-center mb-6">
            <select value={selectedClass} onChange={e => setSelectedClass(e.target.value)}
              className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white">
              {classes.map(c => <option key={c._id} value={c._id}>{c.className} - {c.section}</option>)}
            </select>
            <button onClick={() => openCreateModal()}
              className="px-5 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium shadow">
              + Add Materials
            </button>
          </div>

          {loading ? <p>Loading...</p> : materials.length === 0 ? (
            <div className="bg-white rounded-xl shadow p-10 text-center">
              <div className="text-5xl mb-4">📚</div>
              <h3 className="text-lg font-semibold text-gray-700">No Materials Assigned</h3>
              <p className="text-gray-500">Add books and uniform items for this class</p>
            </div>
          ) : (
            <div className="grid gap-6">
              {materials.map(mat => (
                <div key={mat._id} className="bg-white rounded-xl shadow-sm border overflow-hidden">
                  <div className="px-6 py-4 bg-gradient-to-r from-indigo-50 to-blue-50 flex flex-wrap justify-between items-center">
                    <div>
                      <h3 className="font-bold text-lg text-gray-800">{mat.classId?.className} - {mat.classId?.section}</h3>
                      <p className="text-sm text-gray-500">AY: {mat.academicYear}</p>
                    </div>
                    <div className="flex gap-2 mt-2 sm:mt-0">
                      <button onClick={() => openCreateModal(mat)}
                        className="px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 font-medium">✏️ Edit</button>
                      <button onClick={() => handleDelete(mat._id)}
                        className="px-3 py-1.5 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200 font-medium">🗑️ Delete</button>
                    </div>
                  </div>

                  <div className="p-6 grid md:grid-cols-2 gap-6">
                    {/* Books */}
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        📖 Books <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">{mat.books.length}</span>
                      </h4>
                      {mat.books.length === 0 ? <p className="text-gray-400 text-sm">No books added</p> : (
                        <div className="space-y-2">
                          {mat.books.map((b, i) => (
                            <div key={i} className="flex justify-between items-center bg-gray-50 rounded-lg px-3 py-2">
                              <div>
                                <p className="font-medium text-gray-800 text-sm">{b.name}</p>
                                <p className="text-xs text-gray-500">{b.subject} {b.author && `• ${b.author}`}</p>
                              </div>
                              <div className="text-right">
                                <span className="font-semibold text-green-700 text-sm">₹{b.price || 0}</span>
                                {b.isRequired && <span className="block text-xs text-red-500">Required</span>}
                              </div>
                            </div>
                          ))}
                          <p className="text-right font-bold text-sm text-gray-700 pt-2 border-t">
                            Total: ₹{totalBooksCost(mat.books)}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Uniform */}
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        👔 Uniform & Shoes <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">{mat.uniformItems.length}</span>
                      </h4>
                      {mat.uniformItems.length === 0 ? <p className="text-gray-400 text-sm">No uniform items</p> : (
                        <div className="space-y-2">
                          {mat.uniformItems.map((u, i) => (
                            <div key={i} className="bg-gray-50 rounded-lg px-3 py-2">
                              <div className="flex justify-between items-center">
                                <div>
                                  <p className="font-medium text-gray-800 text-sm">{u.itemName} <span className="text-xs text-gray-500">({UNIFORM_TYPES[u.type] || u.type})</span></p>
                                  <p className="text-xs text-gray-500">
                                    {u.gender === 'all' ? 'All' : u.gender === 'boys' ? '👦 Boys' : '👧 Girls'}
                                    {u.color && ` • Color: ${u.color}`}
                                  </p>
                                </div>
                                {u.isRequired && <span className="text-xs text-red-500">Required</span>}
                              </div>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {u.availableSizes.map((s, si) => (
                                  <span key={si} className="text-xs bg-white border rounded px-2 py-0.5">
                                    {s.size} — ₹{s.price}
                                  </span>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {mat.notes && (
                    <div className="px-6 pb-4">
                      <p className="text-sm text-gray-600 bg-yellow-50 rounded-lg p-3">📝 {mat.notes}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </main>

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl my-8">
              <div className="px-6 py-4 border-b flex justify-between items-center bg-indigo-600 rounded-t-2xl">
                <h2 className="text-xl font-bold text-white">📚 Books & Uniform</h2>
                <button onClick={() => setShowModal(false)} className="text-white hover:text-gray-200 text-2xl">&times;</button>
              </div>

              <form onSubmit={handleSubmit} className="max-h-[80vh] overflow-y-auto">
                {error && <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-3 m-4 rounded text-sm">{error}</div>}

                <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4 border-b">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Class</label>
                    <select value={formData.classId} onChange={e => setFormData(p => ({ ...p, classId: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2">
                      {classes.map(c => <option key={c._id} value={c._id}>{c.className} - {c.section}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Academic Year</label>
                    <input type="text" value={formData.academicYear}
                      onChange={e => setFormData(p => ({ ...p, academicYear: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2" />
                  </div>
                </div>

                {/* Tabs */}
                <div className="flex border-b">
                  <button type="button" onClick={() => setActiveTab('books')}
                    className={`flex-1 py-3 text-sm font-medium ${activeTab === 'books' ? 'border-b-2 border-indigo-600 text-indigo-700 bg-indigo-50' : 'text-gray-500 hover:text-gray-700'}`}>
                    📖 Books ({formData.books.length})
                  </button>
                  <button type="button" onClick={() => setActiveTab('uniform')}
                    className={`flex-1 py-3 text-sm font-medium ${activeTab === 'uniform' ? 'border-b-2 border-purple-600 text-purple-700 bg-purple-50' : 'text-gray-500 hover:text-gray-700'}`}>
                    👔 Uniform & Shoes ({formData.uniformItems.length})
                  </button>
                </div>

                <div className="p-6 space-y-4">
                  {/* Books Tab */}
                  {activeTab === 'books' && (
                    <>
                      {formData.books.map((book, idx) => (
                        <div key={idx} className="border rounded-xl p-4 bg-gray-50 relative">
                          {formData.books.length > 1 && (
                            <button type="button" onClick={() => removeBook(idx)}
                              className="absolute top-2 right-2 text-red-500 hover:text-red-700 text-lg">×</button>
                          )}
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            <div className="col-span-2 md:col-span-1">
                              <label className="block text-xs font-medium text-gray-600 mb-1">Book Name *</label>
                              <input type="text" required value={book.name} onChange={e => updateBook(idx, 'name', e.target.value)}
                                className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="e.g. Mathematics Textbook" />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Subject *</label>
                              <input type="text" required value={book.subject} onChange={e => updateBook(idx, 'subject', e.target.value)}
                                className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="e.g. Maths" />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Author</label>
                              <input type="text" value={book.author} onChange={e => updateBook(idx, 'author', e.target.value)}
                                className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Author name" />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Publisher</label>
                              <input type="text" value={book.publisher} onChange={e => updateBook(idx, 'publisher', e.target.value)}
                                className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Publisher" />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Price (₹)</label>
                              <input type="number" value={book.price} onChange={e => updateBook(idx, 'price', e.target.value)}
                                className="w-full border rounded-lg px-3 py-2 text-sm" min="0" />
                            </div>
                            <div className="flex items-end pb-1">
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input type="checkbox" checked={book.isRequired} onChange={e => updateBook(idx, 'isRequired', e.target.checked)}
                                  className="rounded border-gray-300 text-indigo-600" />
                                <span className="text-sm text-gray-700">Required</span>
                              </label>
                            </div>
                          </div>
                        </div>
                      ))}
                      <button type="button" onClick={addBook}
                        className="w-full py-2 border-2 border-dashed border-indigo-300 rounded-xl text-indigo-600 hover:bg-indigo-50 font-medium text-sm">
                        + Add Another Book
                      </button>
                    </>
                  )}

                  {/* Uniform Tab */}
                  {activeTab === 'uniform' && (
                    <>
                      {formData.uniformItems.map((item, idx) => (
                        <div key={idx} className="border rounded-xl p-4 bg-gray-50 relative">
                          <button type="button" onClick={() => removeUniform(idx)}
                            className="absolute top-2 right-2 text-red-500 hover:text-red-700 text-lg">×</button>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Item Name *</label>
                              <input type="text" required value={item.itemName} onChange={e => updateUniform(idx, 'itemName', e.target.value)}
                                className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="e.g. School Shirt" />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Type *</label>
                              <select value={item.type} onChange={e => updateUniform(idx, 'type', e.target.value)}
                                className="w-full border rounded-lg px-3 py-2 text-sm">
                                {Object.entries(UNIFORM_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Gender</label>
                              <select value={item.gender} onChange={e => updateUniform(idx, 'gender', e.target.value)}
                                className="w-full border rounded-lg px-3 py-2 text-sm">
                                <option value="all">All</option>
                                <option value="boys">Boys</option>
                                <option value="girls">Girls</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Color</label>
                              <input type="text" value={item.color} onChange={e => updateUniform(idx, 'color', e.target.value)}
                                className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="e.g. White" />
                            </div>
                          </div>
                          <div className="flex items-center gap-2 mb-2">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input type="checkbox" checked={item.isRequired} onChange={e => updateUniform(idx, 'isRequired', e.target.checked)}
                                className="rounded border-gray-300 text-indigo-600" />
                              <span className="text-sm text-gray-700">Required</span>
                            </label>
                          </div>

                          {/* Sizes */}
                          <div className="border-t pt-3 mt-2">
                            <div className="flex justify-between items-center mb-2">
                              <label className="text-xs font-medium text-gray-600">Available Sizes & Prices <span className="text-red-500">*</span></label>
                              <button type="button" onClick={() => addSize(idx)}
                                className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded hover:bg-purple-200">+ Size</button>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {item.availableSizes.map((sz, sIdx) => (
                                <div key={sIdx} className="flex items-center gap-1 bg-white border border-gray-300 rounded-lg px-2 py-1 focus-within:ring-2 focus-within:ring-purple-500">
                                  <input type="text" required value={sz.size} onChange={e => updateSize(idx, sIdx, 'size', e.target.value)}
                                    className="w-16 border-0 text-sm text-center focus:ring-0 font-medium" placeholder="Size*" title="Size is required" />
                                  <span className="text-gray-400">₹</span>
                                  <input type="number" value={sz.price} onChange={e => updateSize(idx, sIdx, 'price', e.target.value)}
                                    className="w-16 border-0 text-sm text-center focus:ring-0" min="0" />
                                  {item.availableSizes.length > 1 && (
                                    <button type="button" onClick={() => removeSize(idx, sIdx)} className="text-red-400 hover:text-red-600 ml-1">×</button>
                                  )}
                                </div>
                              ))}
                            </div>
                            {item.type === 'shoes' && (
                              <p className="text-xs text-gray-400 mt-1">💡 Common shoe sizes: 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12</p>
                            )}
                            {['shirt', 'pant', 'skirt', 'blazer', 'sweater', 'pe_shirt', 'pe_pant'].includes(item.type) && (
                              <p className="text-xs text-gray-400 mt-1">💡 Common sizes: XS, S, M, L, XL, XXL or 22, 24, 26, 28, 30, 32, 34, 36, 38, 40</p>
                            )}
                          </div>
                        </div>
                      ))}
                      <button type="button" onClick={addUniformItem}
                        className="w-full py-2 border-2 border-dashed border-purple-300 rounded-xl text-purple-600 hover:bg-purple-50 font-medium text-sm">
                        + Add Uniform / Shoes Item
                      </button>
                    </>
                  )}

                  {/* Notes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
                    <textarea value={formData.notes} onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))}
                      rows={2} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Any additional instructions..." />
                  </div>
                </div>

                <div className="flex justify-end gap-3 px-6 py-4 border-t">
                  <button type="button" onClick={() => setShowModal(false)}
                    className="px-5 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
                  <button type="submit"
                    className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium shadow">
                    💾 Save Materials
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PrincipalMaterials;
